import { FieldState } from "@homebound/form-state";
import { SyntheticEvent, useState } from "react";
import { currencyFormatterWithCents, dollarsToCents, useComputed } from "~utils";
import { TextField, TextFieldProps } from "./TextField";

// "cents": value lives as integer cents, displays as decimal dollars (e.g. 12345 → "123.45").
// undefined: passes through as a plain integer (e.g. days, counts).
export type BoundNumberFieldType = "cents";

// Generic over V so callers can pass `FieldState<number>` (non-nullable form value) or
// `FieldState<number | undefined>` / `FieldState<number | null | undefined>` interchangeably.
export type BoundNumberFieldProps<V extends number | null | undefined> = Omit<
  TextFieldProps,
  "value" | "onChange" | "type" | "onFocus" | "onBlur"
> & {
  field: FieldState<V>;
  type?: BoundNumberFieldType;
  onFocus?: (e: SyntheticEvent) => void;
  onBlur?: (e: SyntheticEvent) => void;
};

/**
 * Minimal `TextField` wrapper that binds to a `form-state` `FieldState<number>`.
 *
 * Key behaviors (matches Beam's `BoundNumberField` for our subset of needs):
 * - Reads `field.value` reactively via `useComputed` so external updates re-render us.
 * - Calls `field.focus()` / `field.blur()` so form-state's autoSave only fires on blur,
 *   not on every keystroke as the user types.
 * - With `type="cents"`, transparently converts between cents (storage) and dollars (display).
 * - Keeps a local "wip" string while focused so the user's in-progress text isn't reformatted
 *   on each render (e.g. typing "1.2" doesn't snap to "1.20" until blur).
 */
export function BoundNumberField<V extends number | null | undefined>(props: BoundNumberFieldProps<V>) {
  const { field, type, onFocus, onBlur, ...others } = props;

  const value = useComputed(() => field.value, [field]);
  const [wip, setWip] = useState<string | undefined>(undefined);

  const formatted = type === "cents" ? currencyFormatterWithCents(value) : value == null ? "" : String(value);
  const display = wip ?? formatted;

  return (
    <TextField
      {...others}
      type={type === "cents" ? "text" : "number"}
      inputMode={type === "cents" ? "decimal" : undefined}
      data-testid={field.key}
      value={display}
      onFocus={(e) => {
        field.focus();
        setWip(formatted);
        onFocus?.(e);
      }}
      onChange={(_, raw) => {
        setWip(raw);
        const parsed = type === "cents" ? dollarsToCents(raw) : raw === "" ? undefined : Number(raw);
        // Cast: `field`'s `V` is constrained to `number | null | undefined`, and `parsed` is one of those.
        // TypeScript can't narrow that V accepts our parsed value, so we widen here.
        field.set(parsed as V);
      }}
      onBlur={(e) => {
        setWip(undefined);
        field.blur();
        onBlur?.(e);
      }}
    />
  );
}
